import {
  MessageAppend,
  TransactionButtonProps,
  TransactionContainerProps,
} from '@bitte-ai/types';

import { X } from 'lucide-react';
import { useState } from 'react';
import { useTransaction } from '../../../hooks/useTransaction';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { createDeclineTransactionMessage } from '../../../lib/transaction-messages';
import { shortenString } from '../../../lib/utils';
import { useAccount } from '../../../state/wallet.state';
import { Button } from '../../ui/button';
import { CardFooter, CardHeader } from '../../ui/card';
import { CopyStandard } from '../CopyStandard';
import DefaultTxApproveButton from '../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../default-components/DefaultTxDeclineButton';
import LoadingMessage from '../loading/LoadingMessage';
import { TransactionDetail } from './TransactionDetail';

export interface CardanoTxCardProps {
  cardanoData?: any;
  messageBackgroundColor: string;
  borderColor: string;
  textColor: string;
  customTxContainer?: React.ComponentType<TransactionContainerProps>;
  customApproveTxButton?: React.ComponentType<TransactionButtonProps>;
  customDeclineTxButton?: React.ComponentType<TransactionButtonProps>;
  append: MessageAppend;
  messageId: string;
}

export const CardanoTxCard = ({
  cardanoData,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
}: CardanoTxCardProps) => {
  const { width } = useWindowSize();
  const isMobile = !!width && width < 640;
  const account = useAccount();
  const cardanoWallet = (account as any).cardanoWallet;
  const { handleTxn } = useTransaction({
    cardanoWallet,
  } as any);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [transaction, setTransaction] = useState<{
    txHash?: string;
    status?: string;
    error?: string;
  } | null>(null);
  const [isDeclined, setIsDeclined] = useState(false);

  // Extract the actual cardano sign request from the nested structure
  const actualCardanoData = cardanoData?.cardanoSignRequest || cardanoData;

  if (
    !actualCardanoData ||
    (!actualCardanoData.unsignedTransaction &&
      !actualCardanoData.transactionBytes)
  ) {
    return (
      <p className="bitte-my-4 bitte-overflow-auto bitte-text-center">
        Unable to create Cardano transaction.
      </p>
    );
  }

  const handleSmartAction = async () => {
    if (!cardanoWallet) {
      setErrorMsg('Cardano wallet not connected');
      return;
    }

    // Access the wallet instance that has the signing methods
    const walletInstance =
      cardanoWallet.walletInstance || cardanoWallet._walletInstance;

    if (!walletInstance) {
      setErrorMsg('Cardano wallet instance not available');
      return;
    }

    if (!walletInstance.signTx || !walletInstance.submitTx) {
      setErrorMsg('Cardano wallet methods not available');
      return;
    }

    const transactionData =
      actualCardanoData.unsignedTransaction ||
      actualCardanoData.transactionBytes;
    if (!transactionData) {
      setErrorMsg('No transaction data provided');
      return;
    }

    setIsLoading(true);
    setErrorMsg('');

    try {
      const result = await handleTxn({
        cardanoData: actualCardanoData,
      } as any);

      const cardanoResult = (result as any).cardano;
      if (cardanoResult?.txHash) {
        setTransaction({
          txHash: cardanoResult.txHash,
          status: 'success',
        });

        await append({
          role: 'system',
          content: JSON.stringify({
            messageId,
            status: 'success',
            txHash: cardanoResult.txHash,
            blockchain: 'cardano',
          }),
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Transaction failed';
      setErrorMsg(errorMessage);
      setTransaction({
        status: 'error',
        error: errorMessage,
      });

      await append({
        role: 'system',
        content: JSON.stringify({
          messageId,
          status: 'fail',
          error: errorMessage,
          blockchain: 'cardano',
        }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadingIndicator = (
    <div>
      <LoadingMessage color={textColor} />
      <p className="bitte-text-center bitte-text-sm">
        Transaction for{' '}
        {shortenString(
          actualCardanoData.unsignedTransaction ||
            actualCardanoData.transactionBytes ||
            '',
          8,
        )}{' '}
        in progress
      </p>
    </div>
  );

  const handleDecline = async () => {
    await append(createDeclineTransactionMessage(messageId));
    setIsDeclined(true);
  };

  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader
        className="bitte-border-b bitte-p-4 bitte-text-center bitte-md:p-6"
        style={{ borderColor: borderColor }}
      >
        <p className="bitte-text-xl bitte-font-semibold">Cardano Transaction</p>
      </CardHeader>

      <div className="bitte-p-6">
        <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
          <TransactionDetail
            label="Wallet Address"
            value={
              cardanoWallet?.address ? (
                <CopyStandard
                  text={cardanoWallet.address}
                  textSize="sm"
                  charSize={isMobile ? 7 : 12}
                />
              ) : (
                'Not available'
              )
            }
          />
          <TransactionDetail
            label="Transaction Data"
            value={
              <CopyStandard
                text={
                  actualCardanoData.unsignedTransaction ||
                  actualCardanoData.transactionBytes ||
                  ''
                }
                textSize="sm"
                charSize={isMobile ? 10 : 15}
              />
            }
          />
          {actualCardanoData.network && (
            <TransactionDetail
              label="Network"
              value={actualCardanoData.network}
            />
          )}
        </div>
      </div>

      {errorMsg || (transaction?.status === 'error' && transaction?.error) ? (
        <div className="bitte-flex bitte-flex-col bitte-items-center bitte-gap-4 bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm bitte-break-all">
          <p className="bitte-text-red-300">
            {transaction?.status === 'error' && transaction?.error
              ? transaction.error
              : `An error occurred trying to execute your transaction: ${errorMsg}.`}
          </p>
          <Button
            className="bitte-w-1/2"
            variant="outline"
            onClick={() => {
              setErrorMsg('');
              // Clear error state from transaction if needed
              if (transaction?.status === 'error') {
                setTransaction((prev) =>
                  prev
                    ? {
                        ...prev,
                        error: undefined,
                      }
                    : null,
                );
              }
            }}
          >
            Dismiss
          </Button>
        </div>
      ) : isLoading && !transaction?.txHash ? (
        loadingIndicator
      ) : null}

      {isDeclined ? (
        <div className="bitte-p-6 bitte-text-center">
          <X className="bitte-mx-auto bitte-mb-2 bitte-h-8 bitte-w-8 bitte-text-red-500" />
          <p>Transaction declined</p>
        </div>
      ) : null}

      {transaction?.txHash ? (
        <div className="bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm bitte-break-all">
          <p className="bitte-text-green-300">
            Transaction successful! Hash: {transaction.txHash}
          </p>
        </div>
      ) : null}

      {!isLoading && !errorMsg && !transaction?.txHash && !isDeclined ? (
        <CardFooter className="bitte-flex bitte-items-center bitte-gap-6">
          <>
            <DeclineButton onClick={handleDecline} disabled={isLoading} />
            <ApproveButton
              onClick={handleSmartAction}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </>
        </CardFooter>
      ) : null}
    </TxContainer>
  );
};
